import { useState } from 'react';
import {
  BUILTIN_KINDS,
  legacyTypeForKind,
  normalizeKindId,
  slugify,
  type Adventure,
  type KindDef,
  type Section,
} from '@gm-binder/shared';
import { Button, IconButton, Input, Card, Badge, Modal, Switch } from '../ui';
import { KindBuilder } from './KindBuilder';
import { useEditorStore } from '../../store/editorStore';
import styles from './SectionManager.module.css';

/** Optional prefab kinds offered by the "Aggiungi sezione" → Prefab picker (plan §3b). */
const OPTIONAL_PREFABS = ['overview', 'clue', 'item', 'table', 'page'] as const;

function sectionKind(s: Section): string {
  return normalizeKindId(s.kind ?? s.type ?? 'custom');
}

/** Re-assign contiguous order values: adventure group first, system group after. */
function renumber(adv: Adventure): void {
  const groups: ('adventure' | 'system')[] = ['adventure', 'system'];
  let order = 0;
  for (const g of groups) {
    adv.sections
      .filter((s) => (s.scope ?? 'adventure') === g)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .forEach((s) => {
        s.order = order++;
      });
  }
}

function makeSection(adv: Adventure, kind: string): Section {
  const def = BUILTIN_KINDS[kind];
  const scope = (def?.scope ?? 'adventure') as 'adventure' | 'system';
  const base: Section = {
    id: kind,
    kind,
    type: legacyTypeForKind(kind) as Section['type'],
    title: def?.label ?? kind,
    icon: def?.icon ?? '📄',
    scope,
    fixed: def?.fixed ?? false,
    enabled: true,
    order: adv.sections.length,
  };
  if (kind === 'page') {
    const n = adv.pages.length + 1;
    const pageId = `page-${n}`;
    adv.pages.push({ id: pageId, title: `Pagina ${n}`, markdown: '' });
    base.id = pageId;
    base.title = `Pagina ${n}`;
    base.pageId = pageId;
  }
  return base;
}

export function SectionManager() {
  const adventure = useEditorStore((s) => s.adventure);
  const update = useEditorStore((s) => s.update);
  const [adding, setAdding] = useState<null | 'menu' | 'prefab' | 'custom'>(null);

  if (!adventure) return null;

  const sections = [...adventure.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const adventureSections = sections.filter((s) => (s.scope ?? 'adventure') === 'adventure');
  const systemSections = sections.filter((s) => (s.scope ?? 'adventure') === 'system');
  const presentKinds = new Set(sections.map(sectionKind));

  const setSection = (id: string, patch: Partial<Section>) =>
    update((adv) => {
      const s = adv.sections.find((x) => x.id === id);
      if (s) Object.assign(s, patch);
    });

  const move = (id: string, dir: -1 | 1) =>
    update((adv) => {
      const target = adv.sections.find((x) => x.id === id);
      if (!target) return;
      const group = adv.sections
        .filter((x) => (x.scope ?? 'adventure') === (target.scope ?? 'adventure'))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const idx = group.findIndex((x) => x.id === id);
      const swap = group[idx + dir];
      if (!swap) return;
      const tmp = target.order;
      target.order = swap.order;
      swap.order = tmp;
      renumber(adv);
    });

  const remove = (id: string) =>
    update((adv) => {
      adv.sections = adv.sections.filter((x) => x.id !== id);
      renumber(adv);
    });

  const addPrefab = (kind: string) =>
    update((adv) => {
      adv.sections.push(makeSection(adv, kind));
      renumber(adv);
    });

  const addCustom = (kind: KindDef) => {
    update((adv) => {
      adv.kinds = { ...adv.kinds, [kind.id]: kind };
      adv.sections.push({
        id: kind.id,
        kind: kind.id,
        type: 'custom',
        title: kind.label,
        icon: kind.icon,
        scope: kind.scope,
        fixed: false,
        enabled: true,
        order: adv.sections.length,
      });
      renumber(adv);
    });
    setAdding(null);
  };

  const availablePrefabs = OPTIONAL_PREFABS.filter((k) => k === 'page' || !presentKinds.has(k));
  const kindOptions = [
    ...Object.values(BUILTIN_KINDS).map((k) => ({ id: k.id, label: k.label })),
    ...Object.entries(adventure.kinds).map(([id, def]) => ({
      id,
      label: (def as Partial<KindDef>)?.label ?? id,
    })),
  ];

  const renderRow = (s: Section, group: Section[], idx: number) => (
    <Card key={s.id} className={styles.row}>
      <div className={styles.reorder}>
        <IconButton label="Su" disabled={idx === 0} onClick={() => move(s.id, -1)}>
          ▲
        </IconButton>
        <IconButton
          label="Giù"
          disabled={idx === group.length - 1}
          onClick={() => move(s.id, 1)}
        >
          ▼
        </IconButton>
      </div>
      <Input
        className={styles.iconInput}
        value={s.icon}
        maxLength={4}
        aria-label="Icona"
        onChange={(e) => setSection(s.id, { icon: e.target.value })}
      />
      <Input
        className={styles.titleInput}
        value={s.title}
        aria-label="Titolo sezione"
        onChange={(e) => setSection(s.id, { title: e.target.value })}
      />
      <code className={styles.kindTag}>{sectionKind(s)}</code>
      {s.fixed && <Badge tone="info">fisso</Badge>}
      <div className={styles.rowActions}>
        <Switch
          checked={s.enabled !== false}
          onChange={(c) => setSection(s.id, { enabled: c })}
          label="Attiva"
        />
        <IconButton
          label={s.fixed ? 'Sezione fissa: non eliminabile' : 'Elimina'}
          disabled={s.fixed}
          onClick={() => remove(s.id)}
        >
          🗑️
        </IconButton>
      </div>
    </Card>
  );

  return (
    <div className={styles.manager}>
      <section>
        <h3 className={styles.groupTitle}>Sezioni dell'avventura</h3>
        <div className={styles.group}>
          {adventureSections.map((s, i) => renderRow(s, adventureSections, i))}
        </div>
      </section>

      <div className={styles.divider} />

      <section>
        <h3 className={styles.groupTitle}>Sezioni di sistema</h3>
        <div className={styles.group}>
          {systemSections.map((s, i) => renderRow(s, systemSections, i))}
        </div>
      </section>

      <div className={styles.addBar}>
        <Button variant="primary" icon="＋" onClick={() => setAdding('menu')}>
          Aggiungi sezione
        </Button>
      </div>

      <Modal open={adding === 'menu'} onClose={() => setAdding(null)} title="Aggiungi sezione" width={420}>
        <div className={styles.choice}>
          <button className={styles.choiceCard} onClick={() => setAdding('prefab')}>
            <span className={styles.choiceIcon}>📦</span>
            <strong>Prefab</strong>
            <span>Scegli un tipo di sezione integrato</span>
          </button>
          <button className={styles.choiceCard} onClick={() => setAdding('custom')}>
            <span className={styles.choiceIcon}>🛠️</span>
            <strong>Personalizzata</strong>
            <span>Definisci un nuovo tipo con i suoi campi</span>
          </button>
        </div>
      </Modal>

      <Modal open={adding === 'prefab'} onClose={() => setAdding(null)} title="Sezione prefabbricata" width={460}>
        {availablePrefabs.length === 0 ? (
          <p>Tutti i tipi prefabbricati sono già presenti.</p>
        ) : (
          <div className={styles.prefabGrid}>
            {availablePrefabs.map((k) => {
              const def = BUILTIN_KINDS[k];
              return (
                <button
                  key={k}
                  className={styles.prefabCard}
                  onClick={() => {
                    addPrefab(k);
                    setAdding(null);
                  }}
                >
                  <span className={styles.choiceIcon}>{def?.icon ?? '📄'}</span>
                  <strong>{def?.label ?? k}</strong>
                </button>
              );
            })}
          </div>
        )}
      </Modal>

      <Modal open={adding === 'custom'} onClose={() => setAdding(null)} title="Nuovo tipo personalizzato" width={760}>
        <KindBuilder
          existingKindIds={[...Object.keys(BUILTIN_KINDS), ...Object.keys(adventure.kinds)].map((k) =>
            slugify(k),
          )}
          kindOptions={kindOptions}
          onCreate={addCustom}
          onCancel={() => setAdding(null)}
        />
      </Modal>
    </div>
  );
}
